import React, { useMemo, useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import ExportTableCSV from "../../components/export/ExportTableCSV";
import {
  fetchGeneratedReport,
  exportTableReportAsCSV,
} from "../../sdk/reportsAndanalytics/reportsAndAnalytics";
import { toast } from "react-toastify";
import { PERMISSIONS } from "../../contexts/permissions";
import { PermissionGate } from "../../helpers/PermissionGate";
import useAudit from "../../hooks/useAudit";

const TabularReports = () => {
  const { reportType, reportKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoggedView, setHasLoggedView] = useState(false);
  const reportContext = useMemo(() => {
    // ✅ 1. Prefer navigation state
    if (location.state) {
      return location.state;
    }

    // ✅ 2. Fallback on refresh
    const stored = sessionStorage.getItem("tabularReportContext");
    if (stored) {
      const parsed = JSON.parse(stored);

      // ✅ Safety check: ensure URL matches stored data
      if (parsed.reportKey === reportKey) {
        return parsed;
      }
    }

    return null;
  }, [location.state, reportKey]);

  const [limit, setLimit] = useState(10);
  let [totalPages, setTotalPages] = useState(null);
  const [displayedPage, setDisplayedPage] = useState(1);
  let [currentPage, setCurrentPage] = useState(1);
  const [totalItems, settotalItems] = useState([]);

  const handleLimitChange = (e) => {
    const newItemsPerPage = Number(e.target.value);
    setLimit(newItemsPerPage);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const { logAudit } = useAudit();

  useEffect(() => {
    if (!reportContext) return;

    const loadReport = async () => {
      try {
        setLoading(true);

        const res = await fetchGeneratedReport({
          reportType,
          reportKey,
          startDate: reportContext?.fromDate, // ✅ optional
          endDate: reportContext?.toDate, // ✅ optional
          page: currentPage,
          limit: limit,
        });

        setColumns(res?.columns || []);
        setRows(res?.rows || []);

        logAudit({
          category: "Reports",
          actionCode: "GENERATE_REPORT",
          description: `Generated report (${reportContext.reportLabel})`,
          page: "Reports",
          tab: "Generated Report",
          section: "Report Results",
          metadata: {
            reportName: reportContext.reportLabel,
            reportType,
            fromDate: reportContext.fromDate,
            toDate: reportContext.toDate,
            totalRecords: res?.meta?.totalItems,
          },
        });

        setTotalPages(res?.meta?.totalPages || 1);
        setDisplayedPage(res?.meta?.currentPage || currentPage);
        settotalItems(res?.meta?.totalItems || 0);
      } catch (err) {
        logAudit({
          category: "Reports",
          actionCode: "GENERATE_REPORT_FAILED",
          description: `Failed to generate report (${reportContext?.reportLabel})`,
          page: "Reports",
          tab: "Generated Report",
          section: "Report Results",
          metadata: {
            reportName: reportContext?.reportLabel,
            reportType,
            fromDate: reportContext?.fromDate,
            toDate: reportContext?.toDate,
            route: "/dashboard/reports",
            error: err?.message || "Failed to load report data",
          },
        });

        toast.error(err?.message || "Failed to load report data");
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, [reportContext, reportType, reportKey, limit, currentPage]);

  useEffect(() => {
    if (loading) return;
    if (hasLoggedView) return;
    if (!rows.length) return;

    logAudit({
      category: "Reports",
      actionCode: "VIEW_GENERATED_REPORT",
      description: `Viewed generated report (${reportContext.reportLabel})`,
      page: "Reports",
      tab: "Generated Report",
      section: "Report Results",
      metadata: {
        reportName: reportContext.reportLabel,
        reportType,
      },
    });

    setHasLoggedView(true);
  }, [loading, rows, hasLoggedView]);

  if (!reportContext) {
    return (
      <div className="p-6 text-red-600">
        Report context lost. Please regenerate the report.
      </div>
    );
  }

  const handleBack = () => {
    navigate(-1);
  };

  const { reportLabel } = reportContext;

  const renderPages = () => {
    const pages = [];
    for (let i = 1; i <= Math.min(totalPages, 3); i++) {
      pages.push(
        <div
          key={i}
          className={`w-[36px] cursor-pointer h-[36px] rounded-[10px] flex items-center justify-center ${
            currentPage === i
              ? "bg-[#074073] text-white"
              : "bg-[#F5F5F5] text-[#074073]"
          }`}
          onClick={() => handlePageChange(i)}
        >
          <p>{i}</p>
        </div>,
      );
    }

    return pages;
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Anansi || Reports</title>
      </Helmet>
      <div className="w-[100%] bg-[#F5F6F7] h-full overflow-y-auto">
        <div className="bg-[#F5F6F7] mx-[20px]">
          <div className="flex items-center gap-2">
            <div className="flex items-center text-[#074073] text-[20px] font-normal">
              Reports
            </div>
            <svg
              width="12"
              height="24"
              viewBox="0 0 12 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2.45199 6.58023L3.51299 5.52024L9.29199 11.2972C9.38514 11.3898 9.45907 11.4999 9.50952 11.6211C9.55997 11.7424 9.58594 11.8724 9.58594 12.0037C9.58594 12.1351 9.55997 12.2651 9.50952 12.3863C9.45907 12.5076 9.38514 12.6177 9.29199 12.7102L3.51299 18.4902L2.45299 17.4302L7.87699 12.0052L2.45199 6.58023Z"
                fill="#074073"
              />
            </svg>
            <div className="flex items-center text-[#074073] text-[20px] font-bold">
              {reportType}
            </div>
            <svg
              width="12"
              height="24"
              viewBox="0 0 12 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2.45199 6.58023L3.51299 5.52024L9.29199 11.2972C9.38514 11.3898 9.45907 11.4999 9.50952 11.6211C9.55997 11.7424 9.58594 11.8724 9.58594 12.0037C9.58594 12.1351 9.55997 12.2651 9.50952 12.3863C9.45907 12.5076 9.38514 12.6177 9.29199 12.7102L3.51299 18.4902L2.45299 17.4302L7.87699 12.0052L2.45199 6.58023Z"
                fill="#074073"
              />
            </svg>
            <div className="flex items-center text-[#074073] text-[20px] font-bold">
              {reportLabel} Report
            </div>
          </div>
          <div className="mt-5">
            <button
              onClick={handleBack}
              className="bg-[#074073] h-[40px] text-white text-[16px] w-[140px] rounded flex items-center justify-center gap-[10px] px-[20px]"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1.78em"
                height="1em"
                viewBox="0 0 16 9"
              >
                <path
                  fill="currentColor"
                  d="M12.5 5h-9c-.28 0-.5-.22-.5-.5s.22-.5.5-.5h9c.28 0 .5.22.5.5s-.22.5-.5.5"
                />
                <path
                  fill="currentColor"
                  d="M6 8.5a.47.47 0 0 1-.35-.15l-3.5-3.5c-.2-.2-.2-.51 0-.71L5.65.65c.2-.2.51-.2.71 0c.2.2.2.51 0 .71L3.21 4.51l3.15 3.15c.2.2.2.51 0 .71c-.1.1-.23.15-.35.15Z"
                />
              </svg>
              Back
            </button>
          </div>
          <PermissionGate required={[PERMISSIONS?.RP?.EXPORT]}>
            <div className="flex justify-end mt-8">
              <ExportTableCSV
                reportType={reportType}
                reportKey={reportKey}
                startDate={reportContext?.fromDate}
                endDate={reportContext?.toDate}
                page={currentPage}
                limit={limit}
                logAudit={logAudit}
                exportTableReportAsCSV={exportTableReportAsCSV}
              />
            </div>
          </PermissionGate>
          {/* ✅ TABLE */}
          <div className="mt-5 overflow-x-auto bg-white">
            <table className="min-w-full table-fixed border-collapse">
              {/* ✅ TABLE HEADER */}
              <thead>
                <tr className="bg-[#E3F8FF] h-[44px]">
                  <th className="w-[50px] px-3 text-left">
                    <input type="checkbox" />
                  </th>

                  {columns?.map((col) => (
                    <th
                      key={col?.key}
                      className="px-3 text-left align-middle text-[14px] font-normal text-black truncate"
                    >
                      {col?.label}
                    </th>
                  ))}
                </tr>
              </thead>

              {/* ✅ TABLE BODY */}
              <tbody>
                {loading && (
                  <tr>
                    <td
                      colSpan={columns?.length + 1}
                      className="text-center py-6 text-[#074073]"
                    >
                      Loading report data...
                    </td>
                  </tr>
                )}

                {!loading && rows?.length === 0 && (
                  <tr>
                    <td
                      colSpan={columns?.length + 1}
                      className="text-center py-6 text-gray-500"
                    >
                      No data available for the selected criteria.
                    </td>
                  </tr>
                )}

                {rows?.map((row, idx) => (
                  <tr
                    key={idx}
                    className="h-[44px] border-t border-[#F0F0F0] hover:bg-[#F9FAFB]"
                  >
                    <td className="px-3">
                      <input type="checkbox" />
                    </td>

                    {columns?.map((col) => {
                      const value = row[col?.key];

                      const isIdColumn = col?.key?.toLowerCase().includes("id"); // works for memberId, loan_id, id, etc.

                      return (
                        <td
                          key={col?.key}
                          className="px-3 align-middle text-[14px] truncate text-[#000000] font-normal"
                          title={value}
                        >
                          {isIdColumn && value ? `${value}` : (value ?? "-")}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="h-[70px] w-[97%] px-[20px] mx-auto mb-[20px] flex items-center justify-between">
            <div className="flex items-center gap-[20px]">
              <p className="text-[#074073] !text-[14px]">Rows per page</p>
              <select
                name="page"
                id="page"
                className="w-[71px] border-[1px] px-[5px] h-[30px] focus:outline-none"
                value={limit}
                onChange={handleLimitChange}
              >
                <option value="50">50</option>
                <option value="30">30</option>
                <option value="20">20</option>
                <option value="10">10</option>
              </select>
              <p className="text-[#074073] text-[14px]">
                {displayedPage} / {totalPages} ({totalItems} records)
              </p>
            </div>
            <div className="flex items-center gap-[15px]">
              <p
                onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                className="text-[14px] cursor-pointer text-[#074073]"
              >
                Prev
              </p>
              {renderPages()}
              <div
                className={`w-[36px] cursor-pointer h-[36px] rounded-[10px] flex items-center justify-center ${
                  Number(currentPage) > 3
                    ? "bg-[#074073] text-white"
                    : "bg-[#F5F5F5] text-[#074073]"
                }`}
              >
                <p>{Number(currentPage) > 3 ? currentPage : "..."}</p>
              </div>
              <p
                onClick={() =>
                  handlePageChange(Math.min(currentPage + 1, totalPages))
                }
                className="text-[14px] cursor-pointer text-[#074073]"
              >
                Next
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TabularReports;
